<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import InputField from '@js/components/fields/InputField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { date, object, string } from 'yup';

const { tenant, websiteVariants, semBanner } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    websiteVariants: {
        type: Array,
        required: true,
    },
    semBanner: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const form = reactive({
    websiteVariant: websiteVariants.find(
        ({ id }) => id === semBanner?.websiteVariantId,
    ),
    subject: semBanner ? semBanner.subject : '',
    sentAt: semBanner ? semBanner.sentAt : null,
});

const validationSchema = object({
    websiteVariant: object().required('Ce champ est obligatoire.'),
    subject: string().required('Ce champ est obligatoire.'),
    sentAt: date().nullable(),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.sem.postBanner', {
                tenant: tenant.id,
                ...(semBanner ? { id: semBanner.id } : {}),
            }),
            {
                websiteVariantId: form.websiteVariant.id,
                subject: form.subject,
                sentAt: form.sentAt,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.sem.banners', {
                    tenant: tenant.id,
                }),
            );
        })
        .catch((error) => {
            toastError(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <ComboboxField
                    v-model="form.websiteVariant"
                    label="Site"
                    field-name="websiteVariant"
                    field-id="websiteVariant"
                    :options="websiteVariants"
                    filtered-option-key="name"
                    required
                >
                    <template #optionContent="{ option }">
                        {{ option.name }}
                    </template>
                </ComboboxField>

                <InputField
                    v-model="form.subject"
                    label="Sujet"
                    field-name="subject"
                    field-id="subject"
                    required
                />

                <InputField
                    v-model="form.sentAt"
                    label="Envoyé le"
                    field-name="sentAt"
                    field-id="sentAt"
                    type="date"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="semBanner"
                    :url="
                        route('front.projectManagement.sem.deleteBanner', {
                            tenant: tenant.id,
                            id: semBanner.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(
                                route('front.projectManagement.sem.banners', {
                                    tenant: tenant.id,
                                }),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
